/* eslint-disable */
import { Box, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import { createElement, useEffect, useState } from 'react'
import { DashboardMenuItem, MenuItemLink, MenuProps, useResourceDefinitions, useSidebarState } from 'react-admin'
import { useLocation } from 'react-router-dom'
import { Dot } from '../components/Icons'
import useAutoLogout from '../hooks/useAutoLogout'
import SubMenu from './SubMenu'
import { border, color, xxllncColor } from './themes'

const Menu = ({ dense = false }: MenuProps): JSX.Element => {
  const resourcesDefinitions = useResourceDefinitions()
  const resources = Object.keys(resourcesDefinitions).map(name => resourcesDefinitions[name])
  const [open] = useSidebarState()
  // Accordion: only one submenu group is open at a time. `null` = nothing has
  // been clicked yet, in which case the group whose route the user is on auto-
  // opens. Once the user toggles anything, their choice wins until they toggle
  // again or close it.
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const { pathname } = useLocation()

  useAutoLogout()

  const handleToggle = (menu: string) => {
    setOpenMenu(current => (current === menu ? null : menu))
  }

  const getMenuItems = (object) => {
    // Collect submenu resources per group key so we can render them as one node.
    const groups: Record<string, any[]> = {}
    object.forEach(resource => {
      if (!resource.icon || !resource.options?.submenu) return
      const groupKey = typeof resource.options.submenu === 'string'
        ? resource.options.submenu
        : 'settings'
      if (!groups[groupKey]) groups[groupKey] = []
      groups[groupKey].push(resource)
    })

    // Walk the declaration order; emit each top-level resource in place and each
    // submenu group at the position of its first member. This lets Resources.tsx
    // control the visual order (e.g. Historische data right under Dashboard, Beheer
    // at the bottom) instead of all groups being forced to the end.
    const out: any[] = []
    const seenGroups = new Set<string>()
    object.forEach(resource => {
      if (!resource.icon) return
      if (resource.options?.submenu) {
        const groupKey = typeof resource.options.submenu === 'string'
          ? resource.options.submenu
          : 'settings'
        if (seenGroups.has(groupKey)) return
        seenGroups.add(groupKey)
        out.push({
          name: `submenu.${groupKey}`,
          resources: groups[groupKey].map(p => p.name),
          submenu: groups[groupKey],
        })
      } else {
        out.push(resource)
      }
    })
    return out
  }

  return (
    <StyledBox mt={1} className={classes.root}>
      <div className={classes.spacer}>&nbsp;</div>
      <StyledMenuItemLink
        className={classes.item}
        dense={dense}
        key="dashboard"
        primaryText="Dashboard"
        sidebarIsOpen={!open}
        to="/"
      />
      {getMenuItems(resources).map(resource => {
        if ('submenu' in resource) {
          // Default closed; until the user has interacted, the group that contains
          // the active route auto-opens so they see where they are. Once they
          // toggle anything (`openMenu !== null`), accordion takes over and only
          // the clicked group stays open.
          const containsActiveRoute = resource.submenu.some(
            (item: { name: string }) => pathname === `/${item.name}` || pathname.startsWith(`/${item.name}/`)
          )
          const isOpen = openMenu === null
            ? containsActiveRoute
            : openMenu === resource.name
          return (
            <SubMenu
              key={resource.name}
              handleToggle={() => handleToggle(resource.name)}
              isOpen={isOpen}
              sidebarIsOpen={!open}
              name={resource.name}
              dense={dense}
            >
              {resource.submenu.map(item => (
                <StyledMenuItemLink
                  className={classes.link}
                  classes={classes}
                  dense={dense}
                  key={item.name}
                  leftIcon={<Dot fill={xxllncColor.orange100} />}
                  primaryText={item.options?.label || item.name}
                  sidebarIsOpen={!open}
                  to={`/${item.name}`}
                />
              ))}
            </SubMenu>
          )
        }
        return (
          <StyledMenuItemLink
            className={classes.item}
            dense={dense}
            key={resource.name}
            primaryText={resource.options?.label || resource.name}
            sidebarIsOpen={!open}
            to={`/${resource.name}`}
            leftIcon={createElement(resource.icon)}
          />
        )
      })}
    </StyledBox>
  )
}

const PREFIX = 'xxllncMenu'

const classes = {
  root: `${PREFIX}-root`,
  link: `${PREFIX}-link`,
  branding: `${PREFIX}-branding`,
  logo: `${PREFIX}-logo`,
  lab: `${PREFIX}-lab`,
  active: `${PREFIX}-active`,
  icon: `${PREFIX}-icon`,
  item: `${PREFIX}-item`,
  spacer: `${PREFIX}-spacer`,
  footer: `${PREFIX}-footer`
}

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    overflow: 'hidden',
  },
  [`& .${classes.link}`]: {
    [theme.breakpoints.up('sm')]: {
      color: xxllncColor.dark100,
      fontWeight: 500,
      ['&:hover']: {
        backgroundColor: xxllncColor.opDark10,
      },
      ['&.RaMenuItemLink-active']: {
        backgroundColor: xxllncColor.opDark20,
        color: xxllncColor.dark100,
        fontWeight: 600,
      },
    }
  },
  [`& .${classes.item}`]: {
    padding: '12px 13px',
    ['&:hover']: {
      [theme.breakpoints.up('sm')]: {
        backgroundColor: xxllncColor.opDark10,
        color: xxllncColor.dark100,
      }
    }
  },
  [`& .${classes.branding}`]: {
    display: 'flex',
    flexDirection: 'row',
  },
  [`& .${classes.logo}`]: {
    fill: color.common.inactive,
    width: theme.spacing(10),
    marginTop: theme.spacing(1.3),
    marginLeft: theme.spacing(2.5),
    marginRight: theme.spacing(1.5),
  },
  [`& .${classes.lab}`]: {
    backgroundColor: xxllncColor.opDark80,
    color: xxllncColor.opLight60,
    borderRadius: border.radius.lg,
    fontSize: 9,
    fontWeight: 700,
    marginTop: '14px',
    padding: '5px 8px',
  },
  [`& .${classes.spacer}`]: {
    height: '80px',
  },  
  [`& .${classes.footer}`]: {
    textAlign: 'center',
    color: '#fff',
    position: 'absolute',
    bottom: 10,
    width: '100%',
    left: 0,
    '& ul': {
      listStyleType: 'none',
      padding: 0,
      margin: 0,
    },
    '& li': {
      color: '#fff',
      fontSize: '0.9rem',
    },
    '& a': {
      color: '#fff',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
}))

const StyledMenuItemLink = styled(MenuItemLink)(() => ({
  '&.RaMenuItemLink-active': {
    backgroundColor: xxllncColor.opDark20,
    color: xxllncColor.dark100,
    fontWeight: 600,
  },
}))

export default Menu